import * as React from "react"
import { cva } from "class-variance-authority"
import { CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react"
import { cn } from "../../lib/cn"

type BannerVariant = "success" | "warning" | "danger" | "info"

const bannerVariants = cva(
    "flex gap-3 rounded-lg border p-4",
    {
        variants: {
            variant: {
                success: "bg-state-success-subtle border-state-success/30 text-state-success",
                warning: "bg-state-warning-subtle border-state-warning/30 text-state-warning",
                danger:  "bg-state-danger-subtle  border-state-danger/30  text-state-danger",
                info:    "bg-state-info-subtle    border-state-info/30    text-state-info",
            },
        },
        defaultVariants: {
            variant: "info",
        },
    }
)

const ICONS: Record<BannerVariant, React.ElementType> = {
    success: CheckCircle2,
    warning: AlertTriangle,
    danger:  XCircle,
    info:    Info,
}

export interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
    variant?: BannerVariant
    title?: string
    action?: React.ReactNode
}

/**
 * Banner — inline status notice with role="alert".
 *
 * Use for: error messages, warnings, info notices, and success
 * confirmations rendered inline within page or modal content.
 *
 * Do NOT use for: toast notifications (transient, dismissable),
 * empty states, or banners embedded inside dropdown/popover surfaces
 * where the primitive's self-contained card chrome would create a
 * card-inside-card visual (see FilterModal's location-unavailable
 * notice for an example of the latter — kept hand-rolled intentionally).
 */
const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
    ({ className, variant = "info", title, action, children, ...props }, ref) => {
        const Icon = ICONS[variant]

        return (
            <div
                ref={ref}
                role="alert"
                className={cn(bannerVariants({ variant: variant as BannerVariant }), className)}
                {...props}
            >
                <Icon size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                    {title && (
                        <p className="text-sm font-semibold leading-snug mb-1">{title}</p>
                    )}
                    {children && (
                        <div className="text-sm leading-snug opacity-90">{children}</div>
                    )}
                    {action && (
                        <div className="mt-2">{action}</div>
                    )}
                </div>
            </div>
        )
    }
)
Banner.displayName = "Banner"

export { Banner, bannerVariants }
